import React from 'react';
import { OfferTemplateConfig, TemplatePage } from '../../../../../types/offerTemplate.types';
import { hexToRgba, formatDate } from './pdfRenderUtils';

const A4_WIDTH = 794;
const A4_HEIGHT = 1123;

export interface CoverPageRendererProps {
  page: TemplatePage;
  config: OfferTemplateConfig;
  offer?: any;
  offerType?: 'given' | 'received';
  companyInfo?: any;
}

const CoverPageRenderer: React.FC<CoverPageRendererProps> = ({
  page,
  config,
  offer,
  companyInfo,
}) => {
  const s = page.settings || {};
  const titleText = String(s.titleText ?? '');
  const subtitleText = s.subtitleText != null ? String(s.subtitleText) : '';
  const showDecor = Boolean(s.showDecorCircles);
  const preparedBy = s.preparedBy as { name?: string; email?: string; web?: string } | undefined;

  const customerName =
    offer?.account?.companyName ?? offer?.supplier?.companyName ?? '';
  const offerDate = formatDate(offer?.date);

  const fontStack = `'${config.font}', Arial, sans-serif`;

  return (
    <div
      data-pdf-page="true"
      style={{
        width: A4_WIDTH,
        minHeight: A4_HEIGHT,
        height: A4_HEIGHT,
        position: 'relative',
        overflow: 'hidden',
        boxSizing: 'border-box',
        background: `linear-gradient(145deg, ${config.anaRenk} 0%, ${config.aksanRenk} 100%)`,
        fontFamily: fontStack,
        color: '#fff',
      }}
    >
      {showDecor && (
        <>
          <div
            style={{
              position: 'absolute',
              right: -60,
              top: -60,
              width: 280,
              height: 280,
              borderRadius: '50%',
              backgroundColor: hexToRgba(config.aksanRenk, 0.25),
            }}
          />
          <div
            style={{
              position: 'absolute',
              left: -80,
              bottom: 120,
              width: 200,
              height: 200,
              borderRadius: '50%',
              backgroundColor: hexToRgba('#ffffff', 0.06),
            }}
          />
          <div
            style={{
              position: 'absolute',
              right: 100,
              bottom: -40,
              width: 140,
              height: 140,
              borderRadius: '50%',
              backgroundColor: hexToRgba('#ffffff', 0.1),
            }}
          />
        </>
      )}

      <div
        style={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100%',
          padding: '48px 56px 40px',
        }}
      >
        <div
          style={{
            backgroundColor: hexToRgba('#ffffff', 0.95),
            color: config.anaRenk,
            borderRadius: 8,
            padding: '16px 24px',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 16,
            alignSelf: 'flex-start',
            maxWidth: '100%',
            boxShadow: `0 8px 32px ${hexToRgba('#000000', 0.12)}`,
          }}
        >
          {config.logoUrl ? (
            <img
              src={config.logoUrl}
              alt=""
              style={{ maxHeight: 52, maxWidth: 180, objectFit: 'contain' }}
              crossOrigin="anonymous"
            />
          ) : null}
          <div style={{ fontSize: 20, fontWeight: 700, letterSpacing: 0.3 }}>
            {companyInfo?.name || 'Firma Adı'}
          </div>
        </div>

        <div
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            textAlign: 'center',
            padding: '32px 0 24px',
          }}
        >
          <h1
            style={{
              margin: 0,
              fontSize: 42,
              fontWeight: 800,
              lineHeight: 1.15,
              color: '#fff',
              textShadow: `0 2px 24px ${hexToRgba('#000000', 0.2)}`,
              whiteSpace: 'pre-line',
            }}
          >
            {titleText}
          </h1>
          {subtitleText ? (
            <p
              style={{
                margin: '20px 0 0',
                fontSize: 17,
                fontWeight: 500,
                color: hexToRgba('#ffffff', 0.88),
                maxWidth: 560,
                lineHeight: 1.45,
              }}
            >
              {subtitleText}
            </p>
          ) : null}
          {customerName ? (
            <div
              style={{
                marginTop: 36,
                fontSize: 22,
                fontWeight: 600,
                color: hexToRgba('#ffffff', 0.98),
              }}
            >
              {customerName}
            </div>
          ) : null}
          <div
            style={{
              marginTop: 14,
              fontSize: 15,
              color: hexToRgba('#ffffff', 0.75),
            }}
          >
            {offerDate}
          </div>
        </div>

        <div
          style={{
            marginTop: 'auto',
            paddingTop: 24,
            borderTop: `1px solid ${hexToRgba('#ffffff', 0.25)}`,
            fontSize: 13,
            color: hexToRgba('#ffffff', 0.85),
            lineHeight: 1.6,
          }}
        >
          {preparedBy?.name ? <div style={{ fontWeight: 600 }}>{preparedBy.name}</div> : null}
          {preparedBy?.email ? <div>{preparedBy.email}</div> : null}
          {preparedBy?.web ? <div>{preparedBy.web}</div> : null}
        </div>
      </div>
    </div>
  );
};

export default CoverPageRenderer;
